import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  Eye, 
  Users, 
  Mail, 
  Tag, 
  Settings,
  LogOut,
  Store,
  Menu,
  X,
  MessageSquare,
  UserCog,
  TrendingUp
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { ScrollArea } from '../../components/ui/scroll-area';
// import StaffAIAssistant from '../../components/admin/StaffAIAssistant'; // Commenté pour plus tard

const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN'];
const ORDER_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SUPPORT', 'ADVISOR'];
const LEAD_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SUPPORT'];
const DASHBOARD_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SUPPORT', 'ADVISOR'];
const MESSAGE_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SUPPORT', 'ADVISOR'];
const STAFF_ROLES = ['ADMIN', 'SUPER_ADMIN'];

const NAV_ITEMS = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: DASHBOARD_ROLES },
  { href: '/admin/messages', icon: MessageSquare, label: 'Messages', roles: MESSAGE_ROLES },
  { href: '/admin/orders', icon: ShoppingCart, label: 'Commandes', roles: ORDER_ROLES },
  { href: '/admin/staff', icon: UserCog, label: 'Conseillers', roles: STAFF_ROLES },
  { href: '/admin/sales-performance', icon: TrendingUp, label: 'Performance Ventes', roles: STAFF_ROLES },
  { href: '/admin/products', icon: Package, label: 'Produits', roles: ADMIN_ROLES },
  { href: '/admin/looks', icon: Eye, label: 'Looks', roles: ADMIN_ROLES },
  { href: '/admin/leads', icon: Users, label: 'Leads', roles: LEAD_ROLES },
  { href: '/admin/emails', icon: Mail, label: 'Emails', roles: ADMIN_ROLES },
  { href: '/admin/promotions', icon: Tag, label: 'Promotions', roles: ADMIN_ROLES },
  { href: '/admin/settings', icon: Settings, label: 'Paramètres', roles: ADMIN_ROLES },
];

const getFilteredNavItems = (userRole) => {
  return NAV_ITEMS.filter(item => item.roles.includes(userRole));
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background dark">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 border-b border-border bg-card flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="ml-3 font-semibold">VyaStore Admin</span>
      </header>

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-40 h-full w-64 bg-card border-r border-border transition-transform duration-200",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 lg:h-16 flex items-center px-6 border-b border-border">
            <Link to="/admin" className="font-bold text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">V</span>
              </div>
              <span>VyaStore</span>
            </Link>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {getFilteredNavItems(user?.role).map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/admin' && location.pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      isActive 
                        ? "bg-secondary text-secondary-foreground" 
                        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User & Actions */}
          <div className="border-t border-border p-4 space-y-3">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary/50 hover:text-foreground transition-colors"
            >
              <Store className="h-4 w-4" />
              Voir la boutique
            </Link>
            
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-xs font-medium">
                  {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.first_name || 'Admin'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={handleLogout}
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Staff AI Assistant - Commenté pour plus tard */}
      {/* <StaffAIAssistant /> */}
    </div>
  );
}
